import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Forward, Loader2, Mail, MailOpen } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TRIAGEM_BTN, TRIAGEM_BTN_PRIMARIO } from '@/components/kanban/triagem/triagemTabela';
import { TtEmailPopover } from '@/components/kanban/instalacao/TtEmailPopover';
import { enderecoTtCompleto, faltaParaEmailTt, type DadosTt, type EnderecoTt } from '@/components/kanban/instalacao/ttFixaDados';
import { cn } from '@/lib/utils';
import type { PedidoTriagemFluxo } from '@/components/kanban/fluxo/useTriagemFluxo';

/* O INPUT DA TT FIXA É O E-MAIL À GERENTE DE CONTAS (Douglas 29/10/2026, maquete
   aprovada). A TT não tem input no Conexão: o 1º e-mail pede a ocorrência CA3-PF1,
   o pedido fica no Input esperando a resposta dela (ou o encaminhamento da Claro
   com a atendimento@ em cópia), a cobrança libera a cada 24 h úteis, e o ✓ só
   anda com a resposta. Quem segura de verdade é o fn_pedido_inputado; aqui a
   linha diz em que pé está. */

const PRAZO_HORAS = 24;
const TZ = 'America/Sao_Paulo';

export interface TtInputLinha {
  situacao: ReactNode;
  prazo: ReactNode;
  email: ReactNode;
  liberado: boolean;
}

interface Resposta { chegou_em: string; de_email: string | null; assunto: string | null; resumo: string | null }
interface EstadoTtInput { pedido_id: string; enviados: number; ultimo_em: string | null; horas: number | null; falhou: boolean; resposta: Resposta | null }

const quando = (iso: string) => new Date(iso).toLocaleString('pt-BR', { timeZone: TZ, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(',', '');

async function chamar<T>(path: string, body: unknown): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sua sessão expirou. Entre novamente.');
  const base = ((import.meta.env.VITE_SUPABASE_URL as string) || 'https://beta.crm-api.primetecia.com.br/api').replace(/\/$/, '');
  const r = await fetch(`${base}/v1/input/tt-fixa/${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const corpo = await r.json().catch(() => ({ error: 'O CRM não respondeu corretamente.' }));
  if (!r.ok) throw new Error(corpo.error || 'Não foi possível concluir.');
  return corpo.data as T;
}

const ehTt = (p: PedidoTriagemFluxo) => p.itens.some((i) => i.produto === 'TT FIXA');

function RespostaPopover({ resposta, children }: { resposta: Resposta; children: ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-[380px] p-3" align="end">
        <p className="mb-1 text-xs font-semibold">Resposta da gerente · {quando(resposta.chegou_em)}</p>
        <p className="mb-2 truncate text-[11px] text-muted-foreground" title={`${resposta.de_email ?? ''} · ${resposta.assunto ?? ''}`}>
          {resposta.de_email} · {resposta.assunto}
        </p>
        <p className="max-h-[220px] overflow-y-auto whitespace-pre-wrap rounded-md border border-border px-2.5 py-2 text-[11px] leading-relaxed">
          {resposta.resumo || 'Sem texto: abra o e-mail na caixa atendimento@.'}
        </p>
        <p className="mt-2 text-[10.5px] text-muted-foreground">Confira se a solicitação foi enviada à Claro. Se foi, aperte Input realizado; se a Claro recusou, trate o pedido.</p>
      </PopoverContent>
    </Popover>
  );
}

/** As peças da linha da TT Fixa na Triagem Input, por pedido. Pedido sem TT devolve undefined. */
export function useTtFixaInput(pedidos: PedidoTriagemFluxo[], ativo: boolean) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [aberto, setAberto] = useState<string | null>(null);

  const tt = ativo ? pedidos.filter(ehTt) : [];
  const ids = tt.map((p) => p.pedido_id);
  const idsItens = tt.flatMap((p) => p.itens.filter((i) => i.produto === 'TT FIXA').map((i) => i.item_id));

  const { data: estados = [] } = useQuery({
    queryKey: ['tt-fixa-input-estado', ids.join(',')],
    enabled: ids.length > 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: () => chamar<EstadoTtInput[]>('estado', { pedidoIds: ids }),
  });
  const { data: linhas = [] } = useQuery({
    queryKey: ['tt-fixa-dados', idsItens.join(',')],
    enabled: idsItens.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('itens_pedido').select('id, pedido_id, tt_contrato_pf, endereco_instalacao').in('id', idsItens);
      if (error) throw error;
      return (data || []) as { id: string; pedido_id: string; tt_contrato_pf: string | null; endereco_instalacao: EnderecoTt | null }[];
    },
  });

  const estado = new Map(estados.map((e) => [e.pedido_id, e]));
  const dados = new Map<string, DadosTt>();
  for (const l of linhas) {
    const d = dados.get(l.pedido_id) ?? { contratoPf: '', endereco: {} };
    if (!d.contratoPf && l.tt_contrato_pf?.trim()) d.contratoPf = l.tt_contrato_pf.trim();
    if (l.endereco_instalacao && (enderecoTtCompleto(l.endereco_instalacao) || !enderecoTtCompleto(d.endereco))) d.endereco = l.endereco_instalacao;
    dados.set(l.pedido_id, d);
  }

  const enviar = useMutation({
    mutationFn: async ({ pedido, novos }: { pedido: PedidoTriagemFluxo; novos: DadosTt | null }) => {
      if (novos) {
        const { error } = await supabase.from('itens_pedido')
          .update({ tt_contrato_pf: novos.contratoPf || null, endereco_instalacao: novos.endereco } as never)
          .in('id', pedido.itens.filter((i) => i.produto === 'TT FIXA').map((i) => i.item_id));
        if (error) throw new Error(`Não salvou o contrato PF e o endereço: ${error.message}`);
        queryClient.invalidateQueries({ queryKey: ['tt-fixa-dados'] });
      }
      return chamar<{ numero: number }>(`${pedido.pedido_id}/email`, {});
    },
    onSuccess: (r) => {
      toast({ title: `${r.numero}º e-mail enviado à gerente de contas`, description: `O pedido fica no Input, no fim da fila, até ela responder. Cobrança de novo em ${PRAZO_HORAS} h úteis.` });
      setAberto(null);
      ['tt-fixa-input-estado', 'triagem-fluxo'].forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
    },
    onError: (e) => toast({ title: 'O e-mail não saiu', description: e instanceof Error ? e.message : 'Tente novamente.', variant: 'destructive' }),
  });

  return (pedido: PedidoTriagemFluxo): TtInputLinha | undefined => {
    if (!ativo || !ehTt(pedido)) return undefined;
    const est = estado.get(pedido.pedido_id);
    const d = dados.get(pedido.pedido_id);
    const enviados = est?.enviados ?? 0;
    const horas = Math.floor(est?.horas ?? 0);
    const resposta = est?.resposta ?? null;
    const falta = faltaParaEmailTt(d);
    const venceu = enviados > 0 && !resposta && horas >= PRAZO_HORAS;
    const proximo = enviados + 1;

    const texto = resposta ? `Gerente respondeu ${quando(resposta.chegou_em)} · ver resposta`
      : !enviados ? (falta.length ? `Falta ${falta.join(' e ')} para o e-mail sair` : 'Sem e-mail à gerente: envie o 1º')
        : venceu ? `Sem resposta há ${horas} h · cobrar com o ${proximo}º e-mail`
          : `Aguardando a gerente · ${enviados}º e-mail ${est?.ultimo_em ? quando(est.ultimo_em) : ''}`;
    const tom = resposta ? 'text-[#075E54] dark:text-[#25D366]' : !enviados || venceu ? 'text-primary dark:text-[#C87484]' : 'text-muted-foreground';
    const dot = resposta ? 'bg-[#075E54] dark:bg-[#25D366]' : !enviados || venceu ? 'bg-primary dark:bg-[#C87484]' : 'border-[1.5px] border-muted-foreground';
    const linhaSituacao = (
      <span className={cn('flex min-w-0 items-center gap-1 truncate text-[10.5px] leading-tight', tom, resposta && 'font-semibold')}
        title={`${texto}${est?.falhou ? ' · o último e-mail não saiu, tente de novo' : ''}`}>
        <span className={cn('h-[6px] w-[6px] flex-shrink-0 rounded-full', dot)} />
        <span className="truncate">{texto}{est?.falhou ? ' · o último não saiu' : ''}</span>
      </span>
    );
    const situacao = resposta
      ? <RespostaPopover resposta={resposta}><button type="button" className="block min-w-0 max-w-full text-left hover:underline">{linhaSituacao}</button></RespostaPopover>
      : linhaSituacao;

    const prazo = (
      <span className={cn('block truncate text-[11px] tabular-nums',
        venceu ? 'font-bold text-primary dark:text-[#C87484]' : 'font-semibold text-muted-foreground')}
        title={`TT Fixa: a gerente tem ${PRAZO_HORAS} h úteis (segunda a sexta) para responder a cada e-mail.`}>
        {resposta ? `${enviados} e-mail${enviados !== 1 ? 's' : ''}`
          : !enviados ? 'sem e-mail'
            : venceu ? `atrasado há ${horas - PRAZO_HORAS} h` : `${horas} h de ${PRAZO_HORAS} h`}
      </span>
    );

    const enviando = enviar.isPending && enviar.variables?.pedido.pedido_id === pedido.pedido_id;
    let email: ReactNode;
    if (resposta) {
      email = (
        <RespostaPopover resposta={resposta}>
          <Button size="icon" variant="ghost" className={TRIAGEM_BTN} title="Ver a resposta da gerente"><MailOpen /></Button>
        </RespostaPopover>
      );
    } else if (enviados && !venceu) {
      email = (
        <Button size="icon" variant="ghost" className={TRIAGEM_BTN} disabled
          title={`Aguardando a gerente: a cobrança (${proximo}º e-mail) libera com ${PRAZO_HORAS} h úteis do último envio`}>
          <Mail />
        </Button>
      );
    } else {
      const titulo = proximo === 1 ? 'Pedir a ocorrência à gerente (1º e-mail)' : `Cobrar a gerente: enviar o ${proximo}º e-mail`;
      const descricao = (proximo === 1
        ? 'Sai pela caixa atendimento@ para a gerente de contas, no modelo do pedido de transferência de titularidade (CA3-PF1): contrato, cidade, cedente e cessionário, com o pedido de resposta em destaque.'
        : `Já foram ${enviados} e-mail(s), o último em ${est?.ultimo_em ? quando(est.ultimo_em) : '-'}. O ${proximo}º diz que é cobrança e cita os anteriores.`)
        + ' O pedido fica no Input, no fim da fila, até ela responder.';
      email = (
        <TtEmailPopover aberto={aberto === pedido.pedido_id} onAberto={(o) => setAberto(o ? pedido.pedido_id : null)}
          titulo={titulo} descricao={descricao} proximo={proximo} dados={d} salvando={false} enviando={enviar.isPending}
          onEnviar={(novos) => enviar.mutate({ pedido, novos })}>
          <Button size="icon" variant="ghost" className={venceu || !enviados ? TRIAGEM_BTN_PRIMARIO : TRIAGEM_BTN} title={titulo}>
            {enviando ? <Loader2 className="animate-spin" /> : proximo === 1 ? <Mail /> : <Forward />}
          </Button>
        </TtEmailPopover>
      );
    }

    return { situacao, prazo, email, liberado: !!resposta };
  };
}
